/**
 * CandidateOnlinePresence — recruiter-curated catalogue of a candidate's
 * online links with on-demand AI analysis per link + recruiter comments.
 *
 * Mounted on the Overview accordion section. Renders nothing for interviewer
 * role (the parent strips access already).
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Globe, Plus, Trash2, Sparkles, Pencil, X, Save, ExternalLink, Linkedin, Github, Twitter, Facebook, Instagram, Youtube } from "lucide-react";

const LINK_KINDS = [
    ["linkedin", "LinkedIn", Linkedin],
    ["github", "GitHub", Github],
    ["twitter", "Twitter / X", Twitter],
    ["facebook", "Facebook", Facebook],
    ["instagram", "Instagram", Instagram],
    ["youtube", "YouTube", Youtube],
    ["portfolio", "Portfolio site", Globe],
    ["blog", "Blog", Globe],
    ["personal_site", "Personal site", Globe],
    ["behance", "Behance", Globe],
    ["dribbble", "Dribbble", Globe],
    ["stackoverflow", "Stack Overflow", Globe],
    ["medium", "Medium", Globe],
    ["tiktok", "TikTok", Globe],
    ["other", "Other", Globe],
];

function detectKind(url) {
    const s = (url || "").toLowerCase();
    for (const [k] of LINK_KINDS) {
        if (k === "other") continue;
        if (s.includes(k)) return k;
        if (k === "twitter" && (s.includes("x.com") || s.includes("twitter.com"))) return "twitter";
    }
    return "other";
}

function iconForKind(kind) {
    const found = LINK_KINDS.find(([k]) => k === kind);
    return found ? found[2] : Globe;
}

function shortDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; }
}

export default function CandidateOnlinePresence({ candidateId }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [newUrl, setNewUrl] = useState("");
    const [newKind, setNewKind] = useState("other");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/candidates/${candidateId}/online-links`);
            setItems(data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [candidateId]);

    const add = async () => {
        if (!newUrl.trim()) return;
        try {
            await api.post(`/candidates/${candidateId}/online-links`, {
                url: newUrl.trim(),
                kind: newKind === "other" ? detectKind(newUrl) : newKind,
                recruiter_comment: "",
            });
            setNewUrl(""); setNewKind("other"); setAdding(false);
            load();
            toast.success("Link added");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <section className="border border-border bg-white" data-testid="online-presence">
            <header className="px-3 sm:px-4 py-2.5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2">
                    <Globe className="w-4 h-4 text-ikb" />
                    <h3 className="font-display font-bold text-sm sm:text-base">Online presence</h3>
                    <span className="text-[10px] font-mono text-muted-foreground">· {items.length}</span>
                </div>
                {!adding && (
                    <button onClick={() => setAdding(true)} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1 inline-flex items-center gap-1 hover:bg-secondary" data-testid="online-add-btn">
                        <Plus className="w-3 h-3" /> Add link
                    </button>
                )}
            </header>
            {adding && (
                <div className="p-3 sm:p-4 border-b border-border bg-secondary/40 space-y-2" data-testid="online-add-form">
                    <input
                        type="url"
                        autoFocus
                        value={newUrl}
                        onChange={(e) => { setNewUrl(e.target.value); setNewKind(detectKind(e.target.value)); }}
                        placeholder="https://linkedin.com/in/…"
                        onKeyDown={(e) => { if (e.key === "Enter") add(); if (e.key === "Escape") setAdding(false); }}
                        className="w-full h-9 px-2 border border-border bg-white text-sm rounded-none outline-none focus:border-ikb"
                        data-testid="online-url"
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                        <select value={newKind} onChange={(e) => setNewKind(e.target.value)} className="h-8 px-2 border border-border bg-white text-xs" data-testid="online-kind">
                            {LINK_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        <button onClick={add} className="text-[10px] font-mono uppercase tracking-widest bg-ikb hover:bg-[#001f7a] text-white px-2 py-1.5 inline-flex items-center gap-1" data-testid="online-save"><Save className="w-3 h-3" /> Add</button>
                        <button onClick={() => { setAdding(false); setNewUrl(""); }} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1.5 inline-flex items-center gap-1"><X className="w-3 h-3" /> Cancel</button>
                    </div>
                </div>
            )}
            {loading ? (
                <div className="p-6 text-center text-xs font-mono text-muted-foreground">Loading…</div>
            ) : items.length === 0 ? (
                <div className="p-6 text-center text-xs font-mono text-muted-foreground">No links yet. Add LinkedIn, GitHub, portfolio sites, etc.</div>
            ) : (
                <ul className="divide-y divide-border" data-testid="online-list">
                    {items.map((it) => <LinkRow key={it.id} item={it} candidateId={candidateId} onChanged={load} />)}
                </ul>
            )}
        </section>
    );
}

function LinkRow({ item, candidateId, onChanged }) {
    const Icon = iconForKind(item.kind);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const [comment, setComment] = useState(item.recruiter_comment || "");

    const analyze = async () => {
        setBusy(true);
        try {
            await api.post(`/candidates/${candidateId}/online-links/${item.id}/analyze`);
            toast.success("AI summary generated");
            onChanged();
        } catch (err) { toast.error(formatApiError(err)); onChanged(); }
        finally { setBusy(false); }
    };

    const remove = async () => {
        if (!window.confirm("Remove this link?")) return;
        try {
            await api.delete(`/candidates/${candidateId}/online-links/${item.id}`);
            onChanged();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const saveComment = async () => {
        try {
            await api.patch(`/candidates/${candidateId}/online-links/${item.id}`, { recruiter_comment: comment });
            setEditing(false);
            onChanged();
            toast.success("Comment saved");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <li className="p-3 sm:p-4 space-y-2" data-testid={`online-row-${item.id}`}>
            <div className="flex items-start gap-2 flex-wrap">
                <Icon className="w-4 h-4 text-foreground/70 mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                    <a href={item.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-ikb hover:underline inline-flex items-center gap-1 break-all" data-testid={`online-link-${item.id}`}>
                        {item.url} <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                    <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                        {item.kind} · added by {item.added_by_name || "—"} · {shortDate(item.added_at)}
                    </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <button onClick={analyze} disabled={busy} className="text-[10px] font-mono uppercase tracking-widest border border-ikb text-ikb bg-white px-2 py-1 inline-flex items-center gap-1 hover:bg-ikb hover:text-white disabled:opacity-50" data-testid={`online-analyze-${item.id}`}>
                        <Sparkles className="w-3 h-3" /> {busy ? "Analyzing…" : (item.ai_summary ? "Re-analyze" : "Analyze with AI")}
                    </button>
                    <button onClick={remove} className="p-1.5 border border-border text-rose-700 hover:bg-rose-50" title="Remove" data-testid={`online-delete-${item.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
            </div>
            {item.ai_summary && (
                <div className="border-l-2 border-ikb bg-blue-50/40 pl-3 py-2 text-[12px]" data-testid={`online-summary-${item.id}`}>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ikb mb-1 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI summary · {shortDate(item.ai_summary_at)}</div>
                    <p className="whitespace-pre-wrap text-foreground/90">{item.ai_summary}</p>
                </div>
            )}
            {item.ai_summary_status === "running" && (
                <div className="text-[11px] font-mono text-muted-foreground" data-testid={`online-running-${item.id}`}>AI analyzing…</div>
            )}
            {item.ai_summary_status === "unavailable" && !item.ai_summary && (
                <div className="text-[11px] font-mono text-amber-700">AI temporarily unavailable — try again later.</div>
            )}
            {/* Recruiter comment */}
            {editing ? (
                <div className="border border-ikb/40 bg-blue-50/30 p-2 space-y-1.5">
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={2}
                        placeholder="Your comment about this link…"
                        autoFocus
                        className="w-full text-[12px] border border-border bg-white p-1.5 rounded-none outline-none focus:border-ikb"
                        data-testid={`online-comment-textarea-${item.id}`}
                    />
                    <div className="flex items-center gap-1.5">
                        <button onClick={saveComment} className="text-[10px] font-mono uppercase tracking-widest bg-ikb hover:bg-[#001f7a] text-white px-2 py-1 inline-flex items-center gap-1" data-testid={`online-comment-save-${item.id}`}><Save className="w-3 h-3" /> Save</button>
                        <button onClick={() => { setEditing(false); setComment(item.recruiter_comment || ""); }} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1 inline-flex items-center gap-1"><X className="w-3 h-3" /> Cancel</button>
                    </div>
                </div>
            ) : item.recruiter_comment ? (
                <div className="text-[12px] border-l-2 border-foreground/30 pl-2 py-1 italic" data-testid={`online-comment-${item.id}`}>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mr-1">Recruiter comment:</span>{item.recruiter_comment}
                    <button onClick={() => setEditing(true)} className="ml-2 text-ikb hover:underline inline-flex items-center gap-0.5 text-[10px] font-mono uppercase tracking-widest" data-testid={`online-comment-edit-${item.id}`}><Pencil className="w-2.5 h-2.5" /> Edit</button>
                </div>
            ) : (
                <button onClick={() => setEditing(true)} className="text-[10px] font-mono uppercase tracking-widest text-ikb/70 hover:text-ikb hover:underline inline-flex items-center gap-1" data-testid={`online-comment-add-${item.id}`}>
                    <Pencil className="w-2.5 h-2.5" /> Add recruiter comment
                </button>
            )}
        </li>
    );
}
